/* eslint-disable no-prototype-builtins */

// React
import React, {
  useContext,
  useRef,
  useEffect,
  useState,
  useCallback,
} from 'react';

// Navigation
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';

// Redux
import * as slices from '../../slices';
import { updateSearchLocationFromWithMyLocation, updateSelectedRoute, updateShowRouteObjs } from "../../slices";
import { memoize } from 'proxy-memoize';
import { useSelector, useDispatch } from 'react-redux';

// External imports
import { Drawer } from '@vladyoslav/drawer';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faChevronUp,
  faChevronDown,
  faPlus,
  faMinus,
  faUpRightAndDownLeftFromCenter,
  faLocationCrosshairs,
  faXmark,
  faArrowLeft,
} from '@fortawesome/pro-solid-svg-icons';
import { faCircleInfo } from '@fortawesome/pro-regular-svg-icons';
import { useMediaQuery } from '@uidotdev/usehooks';
import Button from 'react-bootstrap/Button';
import cloneDeep from 'lodash/cloneDeep';
import Spinner from 'react-bootstrap/Spinner';

// Internal imports
import { addCameraGroups } from '../data/webcams';
import { compareRoutes } from "../data/routes";
import {
  blueLocationMarkup,
  redLocationToMarkup,
  onMoveEnd,
  setLocationPin,
  setZoomPan,
  setZoomPanAnchored,
  toggleMyLocation,
  zoomIn,
  zoomOut,
  fitMap,
  removeOverlays
} from './helpers';
import { layerNameMap, toggleableLayers } from "./enums";
import { loadLayer, loadEventsLayers, updateEventsLayers, enableReferencedLayer } from './layers';
import { FeatureContext, MapContext } from '../../App';
import { resizePanel, renderPanel, togglePanel } from './panels';
import { pointerMoveHandler, resetHoveredStates } from './handlers/hover';
import { pointerClickHandler, resetClickedStates } from './handlers/click';
import { groupNearbyFeatures, applyOverlapOffsets } from "./helpers";
import DistanceLabels from "../routing/DistanceLabels";
import FilterTabs from './filter/FilterTabs';
import RouteSearch from '../routing/RouteSearch';
import NetworkErrorPopup from './errors/NetworkError';
import ServerErrorPopup from './errors/ServerError';
import StaleLinkErrorPopup from './errors/StaleLinkError';
import LocationAccessPopup from './errors/LocationAccessError';
import overrides from '../map/overrides';
import { faAttributionToggleLabel } from './attributionControlLabels';
import { BASE_MAP, MAP_STYLE } from '../../env';

// Map & geospatial imports
import { applyStyle } from 'ol-mapbox-style';
import { fromLonLat, toLonLat, transformExtent } from 'ol/proj';
import { Attribution, defaults, ScaleLine } from 'ol/control';
import Feature from 'ol/Feature';
import Map from 'ol/Map';
import Geolocation from 'ol/Geolocation';
import MVT from 'ol/format/MVT';
import VectorTileLayer from 'ol/layer/VectorTile';
import VectorTileSource from 'ol/source/VectorTile';
import View from 'ol/View';

// Styling
import './Map.scss';
import { routeStyles } from "../data/featureStyleDefinitions";

export default function DriveBCMap(props) {
  /* initialization */
  // Props
  const {
    mapProps: {referenceData, fromCameraList},
    showNetworkError, showServerError, trackedEventsRef,
    loadingLayers, setLoadingLayers, getInitialLoadingLayers
  } = props;

  // width breakpoints
  const viewportSmallScreen = useMediaQuery('only screen and (max-width: 575px)');
  const viewportLargeScreen = useMediaQuery('only screen and (min-width: 768px)');

  const smallScreen = viewportSmallScreen;
  const largeScreen = viewportLargeScreen;

  // Navigation
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();

  let mousePointXClicked = undefined;

  // Context
  const { mapContext, setMapContext } = useContext(MapContext);
  const { featureContext, setFeatureContext } = useContext(FeatureContext);

  // Redux
  const dispatch = useDispatch();
  const {
    feeds: {
      cameras: { filteredList: filteredCameras },
      events: { list: events, filteredList: filteredEvents },
      ferries: { list: ferries, filteredList: filteredFerries },
      weather: { list: currentWeather, filteredList: filteredCurrentWeathers },
      regional: { list: regionalWeather, filteredList: filteredRegionalWeathers },
      hef: { list: hef, filteredList: filteredHef },
      restStops: { list: restStops, filteredList: filteredRestStops },
      dms: { list: dms = [], filteredList: filteredDms = [] } = {},
      borderCrossings: { list: borderCrossings, filteredList: filteredBorderCrossings },
      wildfires: { list: wildfires, filteredList: filteredWildfires },
    },
    advisories: { list: advisories, filteredList: filteredAdvisories },
    routes: { searchLocationFrom, searchLocationTo, selectedRoute, searchedRoutes, showRouteObjs },
    map: { zoom, pan },
    user: { pendingAction }

  } = useSelector(
    useCallback(
      memoize(state => ({
        feeds: {
          cameras: state.feeds.cameras,
          events: state.feeds.events,
          ferries: state.feeds.ferries,
          weather: state.feeds.weather,
          regional: state.feeds.regional,
          hef: state.feeds.hef,
          restStops: state.feeds.restStops,
          borderCrossings: state.feeds.borderCrossings,
          wildfires: state.feeds.wildfires,
          dms: state.feeds.dms,
        },
        advisories: state.cms.advisories,
        routes: state.routes,
        map: state.map,
        user: state.user
      })),
    ),
  );

  // Refs
  const mapLayers = useRef({}); window.mapLayers = mapLayers;
  const overlapGroups = useRef({});
  const geolocation = useRef();
  const hoveredFeature = useRef();
  const searchParamInitialized = useRef();
  const locationPinRef = useRef();
  const locationToPinRef = useRef();
  const mapElement = useRef();
  const mapContainerRef = useRef();
  const mapRef = useRef();
  const mapView = useRef();
  const panel = useRef();
  const myLocationRef = useRef();
  const locationSet = useRef();
  const routingContainerRef = useRef();
  const scaleLineRef = useRef();

  // Initialization flags
  const isInitialMountLocation = useRef();
  const isInitialClickedFeature = useRef();
  const referenceFeatureInitialized = useRef(false);

  // States
  const [mapRendered, setMapRendered] = useState(false);
  const [openTabs, setOpenTabs] = useState(largeScreen);
  const [maximizedPanel, setMaximizedPanel] = useState(false);
  const [myLocationLoading, setMyLocationLoading] = useState(false);
  const [myLocation, setMyLocation] = useState();
  const [advisoriesInView, setAdvisoriesInView] = useState([]);
  const [referenceFeature, updateReferenceFeature] = useState();
  const [selectedFerries, setSelectedFerries] = useState();
  const [routeDetails, setRouteDetails] = useState({
    distance: null,
    distanceUnit: null,
    closures: null,
    majorEvents: null,
    minorEvents: null,
    roadConditions: null,
    chainUps: null,
    advisories: null
  });
  const [showSpinner, setShowSpinner] = useState(false);

  // Workaround for OL handlers not being able to read states
  const [clickedFeature, setClickedFeature] = useState();
  const [staleLinkMessage, setStaleLinkMessage] = useState();
  const [openedFromCameraList, setOpenedFromCameraList] = useState(fromCameraList || false);
  const clickedFeatureRef = useRef();

  useEffect(() => {
    if (!/^\/cameras\/[^/]+/.test(location.pathname)) {
      setOpenedFromCameraList(false);
    }
  }, [location.pathname]);
  const updateClickedFeature = (feature, center=true) => {
    // Remove highlight from feature on click
    if (feature && feature instanceof Feature && feature.get('highlight')) {
      // Remove highlight from feature
      feature.set("highlight", false)

      // Remove highlight from tracked event
      const trackedEvent = trackedEventsRef.current[feature.get('id')];
      if (trackedEvent)
        trackedEvent.highlight = false;
    }

    clickedFeatureRef.current = feature;
    setClickedFeature(feature);

    if (center) {
      updatePosition(feature);
    }
  };

  const handleSetShowRouteObjs = (value) => {
    dispatch(updateShowRouteObjs(value));
  };

  /* Constants for conditional rendering */
  const openPanel =
    !!clickedFeature ||
    searchedRoutes?.length;

  // Matches renderPanel’s RouteDetailsPanel branch (no map feature selected)
  const drawerShowsRouteDetailsPanel =
    !clickedFeature &&
    !!searchedRoutes?.length;

  // Drawer state
  const getSnapPoints = () => {
    if (showRouteObjs && selectedRoute) {
      return !smallScreen ? ['25%', '50%', '80%'] : ['10%', '50%', '88%'];
    } else {
      return !smallScreen ? ['25%', '50%', '80%'] : ['10%', '50%', '100%'];
    }
  };

  // Use a ref to persist the snap point across renders
  const snapPointRef = useRef('50%');
  const [snap, setSnap] = useState('50%');

  // Update both state and ref when snap changes
  const handleSnapChange = useCallback((newSnap) => {
    snapPointRef.current = newSnap;
    setSnap(newSnap);
  }, []);

  const snapPoints = getSnapPoints();

  // Update snap when route details are shown/hidden (only when snap points actually change)
  const prevRouteDetailsActive = useRef(false);
  const prevSnapPoints = useRef(snapPoints);
  const routeDetailsActive = showRouteObjs && selectedRoute;

  useEffect(() => {
    // Only adjust snap if the available snap points have changed
    const snapPointsChanged = JSON.stringify(prevSnapPoints.current) !== JSON.stringify(snapPoints);

    if (snapPointsChanged) {
      // Check if current snap point is valid in new snap points array
      if (!snapPoints.includes(snapPointRef.current)) {
        // Current snap isn't valid, adjust to nearest valid snap
        const currentSnapValue = parseInt(snapPointRef.current);
        const closestSnap = snapPoints.reduce((prev, curr) => {
          return Math.abs(parseInt(curr) - currentSnapValue) < Math.abs(parseInt(prev) - currentSnapValue) ? curr : prev;
        }, snapPoints[0]);
        handleSnapChange(closestSnap);
      }

      prevSnapPoints.current = snapPoints;
    }

    prevRouteDetailsActive.current = routeDetailsActive;
  }, [snapPoints, routeDetailsActive, handleSnapChange]);

  // When opening a new feature, maintain the last used snap point
  useEffect(() => {
    if (openPanel && !largeScreen) {
      // Use the stored snap point reference
      setSnap(snapPointRef.current);
    } else if (!openPanel) {
      // Reset to default when drawer is completely dismissed
      snapPointRef.current = '50%';
      setSnap('50%');
    }
  }, [openPanel, largeScreen]);

  // Track drawer y-position to reposition buttons fixed to draggable mobile panel
  const drawerRef = useRef();
  const [drawerY, setDrawerY] = useState(0);

  useEffect(() => {
    let frame;

    const updatePanelOffset = () => {
      if (drawerRef.current) {
        const transform = getComputedStyle(drawerRef.current).transform;
        if (transform && transform !== 'none') {
          const match = transform.match(/matrix.*\((.+)\)/);
          if (match) {
            const values = match[1].split(', ');
            const translateY = parseFloat(values[5]);

            // Calculate relative movement from initial position
            const relativeY = translateY - drawerRef.current.offsetHeight;

            setDrawerY(relativeY);
          }

        } else {
          setDrawerY(0);
        }

      } else {
        setDrawerY(0);
      }

      frame = requestAnimationFrame(updatePanelOffset);
    };

    frame = requestAnimationFrame(updatePanelOffset);
    return () => cancelAnimationFrame(frame);
  }, []);

  const attributionControlRef = useRef(null);
  if (!attributionControlRef.current) {
    attributionControlRef.current = new Attribution({
      className: 'ol-attribution attribution-control',
      label: faAttributionToggleLabel('expand', faCircleInfo),
      collapseLabel: faAttributionToggleLabel('collapse', faXmark),
    });
  }

  useEffect(() => {
    const el = attributionControlRef.current?.element;
    if (!el) return;

    if (!smallScreen) {
      el.style.transform = '';
      return;
    }

    el.style.transform = `translateY(${drawerY}px)`;
  }, [smallScreen, drawerY]);

  // ScaleLine
  const scaleLineControl = new ScaleLine({ units: 'metric' });
  const updateScaleLineClass = (openTabs, showRouteObjs) => {
    if (!scaleLineRef.current) {
      scaleLineRef.current = scaleLineControl.element;
    }

    const el = scaleLineRef.current;
    el.classList.toggle('tabs-pushed', !!openTabs);
    el.classList.toggle('route-details-open', (showRouteObjs && !clickedFeature));
  }

  useEffect(() => {
    if (!isInitialClickedFeature.current) {
      isInitialClickedFeature.current = true;
      return;
    }

    const featureType = clickedFeature instanceof Feature ? clickedFeature.get('type') : null;

    if (featureType === 'route' && !compareRoutes(clickedFeature.get('route'), selectedRoute)) {
      for (const route of searchedRoutes) {
        if (compareRoutes(route === clickedFeature.get('route'))) {
          dispatch(updateSelectedRoute(route));
        }
      }
    }
  }, [clickedFeature])

  const updatePosition = (feature) => {
    // Do not process empty features, routes and advisories
    if (feature != null && !Array.isArray(feature) && feature.get('type') !== 'route'&& feature.get('type') !== 'advisory') {
      let geometry = feature.getGeometry();

      if (geometry.getType() !== 'Point') { // feature is a line or polygon
        geometry = feature.getProperties().altFeature.getGeometry(); // use the point feature's geometry
      }

      // Center when opened programmatically (deep link), panel from bottom, or clicked within 390px from left
      if (mousePointXClicked == null || mousePointXClicked < 390 || smallScreen) {
        let zoom = mapView.current.getZoom();
        const coords = geometry.flatCoordinates;
        const mapWidth = mapRef.current?.getSize()?.[0] ?? 0;

        // Deep-link camera open: zoom in so the camera is visible
        if (mousePointXClicked == null && feature.get('type') === 'camera' && zoom < 12) {
          zoom = 12;
        }

        // Use anchored pan if panel from bottom or screen smaller than 1000px
        const shouldUseAnchoredPan = mapWidth < 1000 || smallScreen;

        // Center on top half of the screen, if panel open from bottom
        const anchorYFraction = !viewportLargeScreen ? 0.25 : 0.5;

        // Center on right side of screen minus 390px panel, if panel open from left
        const anchorXFraction = anchorYFraction !== 0.25 ? (((mapWidth-390)/2) + 390) / mapWidth : 0.5;

        if (shouldUseAnchoredPan) {
          setZoomPanAnchored(mapRef, mapView, zoom, coords, anchorXFraction, anchorYFraction);

        } else {
          setZoomPan(mapView, zoom, coords);
        }
      }
    }
  };

  const [showLocationAccessError, setShowLocationAccessError] = useState(false);

  const loadMyLocation = () => {
    if (!locationSet.current) {
      setMyLocationLoading(true);
    } else {
      // per DBC22-5228, clicking button always moves to user's current location
      if ('geolocation' in navigator) {
        navigator.geolocation.getCurrentPosition(
          (position) => {
            const { latitude, longitude } = position.coords;
            if (
              position.coords.longitude <= -113.7 &&
              position.coords.longitude >= -139.3 &&
              position.coords.latitude <= 60.1 &&
              position.coords.latitude >= 48.2
            ) {
              setZoomPan(mapView, 9, fromLonLat([longitude, latitude]));
            }
          },
          (error) => {
            // The user has blocked location access
            if (error.code === error.PERMISSION_DENIED) {
              setShowLocationAccessError(true);
              setMyLocationLoading(false);

            } else {
              // Zoom out and center to BC if location not available
              setZoomPan(mapView, 9, fromLonLat([-126.5, 54.2]));
              setMyLocationLoading(false);
            }
          },
          {
            maximumAge: 30000, // 30 seconds
          }
        );
      }

      dispatch(updateSearchLocationFromWithMyLocation([locationSet.current]));
    }
  }

  useEffect(() => {
    if (myLocationLoading) {
      toggleMyLocation(mapRef, mapView, setMyLocationLoading, setMyLocation, setShowLocationAccessError);
    }
  }, [myLocationLoading])

  useEffect(() => {
    if(myLocation) {
      locationSet.current = myLocation;
      dispatch(updateSearchLocationFromWithMyLocation([myLocation]));
    }
  }, [myLocation])

  // DBC22-4575: re-implementing DBC22-3835
  useEffect(() => {
    if (!clickedFeature) return;

    // Fetch layer status
    const featureType = clickedFeature.get('type');
    const layerName = featureType == 'event' ?
      clickedFeature.get('display_category') :
      layerNameMap[featureType];
    const layerActive = mapContext.visible_layers[layerName];

    // If layer is off, close panel
    if (!layerActive) resetClickedStates(null, clickedFeatureRef, updateClickedFeature);

  }, toggleableLayers.map(layer => mapContext.visible_layers[layer]));

  /* useEffect hooks */
  /* Push ScaleLine to the left when tabs are open */
  useEffect(() => {
    updateScaleLineClass(openTabs, showRouteObjs);
  }, [openTabs, showRouteObjs]);

  /* initialization for OpenLayers map */
  useEffect(() => {
    if (mapRef.current) return; // stops map from initializing more than once
    const deniedBefore = localStorage.getItem('geoDenied') === 'true';

    // check if geolocation permission is granted
    if (navigator.permissions) {  // only when permissions API is supported
      navigator.permissions.query({ name: "geolocation" }).then((permissionStatus) => {
        if (!deniedBefore) {
          setShowLocationAccessError(permissionStatus.state === 'denied');
          localStorage.setItem('geoDenied', 'true');
        }
      });
    }

    // Enable referenced layer
    enableReferencedLayer(referenceData, mapContext);

    const tileSource = new VectorTileSource({
      format: new MVT(),
      url: BASE_MAP,
    });

    // base tile map layer
    const vectorLayer = new VectorTileLayer({
      declutter: true,
      source: tileSource,
      style: function(feature, resolution) { return null; }, // avoids displaying blueline default style before style loads
    });

    // highway symbol layer
    const symbolLayer = new VectorTileLayer({
      declutter: true,
      source: tileSource,
      style: function(feature, resolution) { return null; }, // avoids displaying blueline default style before style loads
    });
    // should be highest z-index so that highway symbols are always visible
    symbolLayer.setZIndex(200);

    // initialize starting optional mapLayers
    window.layers = mapLayers;

    // Set map extent (W, S, E, N)
    const extent = [-155.230138, 36.180153, -102.977437, 66.591323];
    const transformedExtent = transformExtent(extent, 'EPSG:4326', 'EPSG:3857');

    // Initialize map view
    // Center
    const sharedPan = searchParams.get('pan');
    const initialCenter = sharedPan ? sharedPan.split(",").map(Number) : pan;

    // Zoom
    const defaultZoom = zoom;
    const sharedZoom = searchParams.get('zoom');
    const initialZoom = sharedZoom ? sharedZoom : defaultZoom;

    mapView.current = new View({
      projection: 'EPSG:3857',
      center: fromLonLat(initialCenter),
      zoom: initialZoom,
      maxZoom: 15,
      minZoom: 5,
      extent: transformedExtent,
      enableRotation: false,
      zoomFactor: 2.2
    });

    // Apply the basemap style from the arcgis resource
    fetch(MAP_STYLE, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
    }).then(function (response) {
      response.json().then(function (glStyle) {
        // const sourceKeys = Object.keys(glStyle.sources);
        // const autoAttribution =
        //   sourceKeys.length > 0 ? glStyle.sources[sourceKeys[0]].attribution : '';
        // const esriPower =
        //  "Powered by <a href='https://www.esri.com/en-us/home' target='_blank'>Esri | </a>";
        // tileSource.setAttributions([esriPower, autoAttribution]);

        const esriPower =
          "Powered by <a href='https://www.esri.com/en-us/home' target='_blank'>Esri</a> | Tiles: <a href='https://catalogue.data.gov.bc.ca/dataset/78895ec6-c679-4837-a01a-8d65876a3da9' target='_blank'>Ministry of Water, Land and Resource Stewardship</a>";
        tileSource.setAttributions([esriPower]);

        // DBC22-2153
        glStyle.metadata['ol:webfonts'] = '/fonts/{font-family}/{fontweight}{-fontstyle}.css';

        // Overrides
        for (const layer of glStyle.layers) {
          overrides.merge(layer, overrides[layer.id] || {});
        }

        // clone the basemap style so we can override the style layers,
        // filtering out everything that isn't a highway symbol.
        const symbolsStyle = {
          ...glStyle,
          layers: glStyle.layers.filter((layer) => (
            layer.id.startsWith('TRANSPORTATION/DRA/Hwy Symbols') ||
            layer.id.startsWith('POLITICAL/Populated Places') ||
            layer.id.startsWith('TRANSPORTATION/DRA/Road Names')
          )),
        };

        Promise.all([
          applyStyle(vectorLayer, glStyle, 'esri'),
          applyStyle(symbolLayer, symbolsStyle, 'esri'),
        ]).then(() => {
          // First rendercomplete is often a white frame (null style). Wait until
          // basemap style is applied and at least one tile has loaded/painted.
          let done = false;
          const markRendered = () => {
            if (done) return;
            done = true;
            setMapRendered(true);
          };

          tileSource.once('tileloadend', () => {
            mapRef.current?.once('rendercomplete', markRendered);
          });
          // Cached tiles / no reload after applyStyle — don't block cameras forever
          setTimeout(markRendered, 2000);
        });
      });
    });

    // create map
    mapRef.current = new Map({
      target: mapElement.current,
      layers: [vectorLayer, symbolLayer],
      view: mapView.current,
      moveTolerance: 7,
      controls: defaults({
        attribution: false,
        zoom: false,
        rotate: false,
      }).extend([scaleLineControl, attributionControlRef.current]),
    });

    geolocation.current = new Geolocation({
      projection: mapView.current.getProjection(),
    });

    mapRef.current.on('moveend', function () {
      if (smallScreen) {
        resetHoveredStates(null, hoveredFeature);
      }

      mapLayers.current?.highwayCams?.get('syncViewport')?.();

      const [lon, lat] = toLonLat(mapView.current.getCenter());

      const params = new URLSearchParams(window.location.search);

      // Zoom/resolution changed, update overlapping feature positions
      if (params.get('zoom') != mapView.current.getZoom()) {
        applyOverlapOffsets(overlapGroups.current, mapView);
      }

      params.set("pan", lon + ',' + lat);
      params.set("zoom", mapView.current.getZoom());
      navigate(`${location.pathname}?${params.toString()}`, { replace: true });

      dispatch(
        slices.updateMapState({
          pan: toLonLat(mapView.current.getCenter()),
          zoom: mapView.current.getZoom(),
        }),
      );
    });

    // Event listeners
    mapRef.current.on('click', async e => {
      const features = mapRef.current.getFeaturesAtPixel(e.pixel, {
        hitTolerance: 20,
      });

      const mousePointX = e.pixel[0];
      mousePointXClicked = mousePointX;

      pointerClickHandler(
        features, clickedFeatureRef, updateClickedFeature,
        mapView, updateRouteDisplay, mapContext
      );
    });

    // Hover states
    mapRef.current.on('pointermove', (e) => {
      pointerMoveHandler(e, mapRef, hoveredFeature);
    });

    // cleanup OL resources, important for performance
    return () => {
      mapRef.current.dispose();
      mapRef.current = null;
      mapLayers.current = {};
    };
  }, []);

  // Auto-open panel from URL params on initial load
  useEffect(() => {
    if (!mapRendered) return;

    const type = searchParams.get('type');
    const id = searchParams.get('id');

    if (!type || !id) return;

    if (type === 'advisory') {
      // Advisories haven't loaded yet — wait for them, don't give up.
      if (!filteredAdvisories || !filteredAdvisories.length) return;
      const advisoryData = filteredAdvisories?.find(a => a.id === parseInt(id));
      if (advisoryData) {
        setTimeout(() => {
          const olFeature = new Feature();
          olFeature.set('type', 'advisory');
          olFeature.set('id', advisoryData.id);
          olFeature.set('data', advisoryData);
          updateClickedFeature(olFeature);
        }, 500);
      }
      else {
        // Advisories loaded but this id genuinely doesn't exist
        setStaleLinkMessage(true);
      }
    }
  }, [mapRendered, filteredAdvisories]);

  /* Map operations on location search */
  useEffect(() => {
    if (searchLocationFrom && searchLocationFrom.length) {
      setLocationPin(
        searchLocationFrom[0].geometry.coordinates,
        blueLocationMarkup,
        mapRef,
        locationPinRef,
        'Location From',
      );

      if (isInitialMountLocation.current === null) {
        // first run of this effector
        // store the initial searchLocationFrom.[0].label so that subsequent
        // runs can be evaluated to detect change in the search from
        isInitialMountLocation.current = searchLocationFrom[0].label;

      // only zoomPan on from location change when to location is NOT set
      } else if (
        isInitialMountLocation.current !== searchLocationFrom[0].label &&
        searchLocationTo.length == 0
      ) {
        isInitialMountLocation.current = false;
        if (mapContext.pendingStartPan) {
          setZoomPan(
            mapView,
            null,
            fromLonLat(searchLocationFrom[0].geometry.coordinates),
          );

          setMapContext({
            ...mapContext,
            pendingStartPan: false,
          })
        }
      }

    } else {
      // initial location was set, so no need to prevent pan/zoom
      isInitialMountLocation.current = false;
      if (locationPinRef.current) {
        mapRef.current.removeOverlay(locationPinRef.current);
      }
    }
  }, [searchLocationFrom]);

  useEffect(() => {
    if (searchLocationTo && searchLocationTo.length) {
      if (locationToPinRef.current) {
        mapRef.current.removeOverlay(locationToPinRef.current);
      }

      setLocationPin(
        searchLocationTo[0].geometry.coordinates,
        redLocationToMarkup,
        mapRef,
        locationToPinRef,
        'Location To',
      );

      const locationToPin = document.querySelectorAll(".ol-selectable")[0];
      if(locationToPin !== undefined){
        locationToPin.classList.remove("ol-overlay-container");
      }
    } else {
      isInitialMountLocation.current = false;
      if (locationToPinRef.current) {
        mapRef.current.removeOverlay(locationToPinRef.current);
      }
    }
  }, [searchLocationTo]);

  /* Triggering handlers based on navigation data */
  useEffect(() => {
    if (referenceFeature && !referenceFeatureInitialized.current) {
      pointerClickHandler(
        [referenceFeature], clickedFeatureRef, updateClickedFeature,
        mapView, updateRouteDisplay, mapContext
      );

      referenceFeatureInitialized.current = true;
    }
  }, [referenceFeature]);

  /* Loading map layers */
  // Route layer
  const updateRouteDisplay = (route) => {
    if (!route || !mapLayers.current.routeLayer) {
      return;
    }

    const routeFeatures = mapLayers.current.routeLayer.getSource().getFeatures();
    for (const feature of routeFeatures) {
      if (compareRoutes(feature.get('route'), route)) {
        feature.set('clicked', true);
        feature.setStyle(routeStyles['active']);
        dispatch(updateSelectedRoute(route));

      } else {
        feature.set('clicked', false);
        feature.setStyle(routeStyles['static']);
      }
    }
  }

  useEffect(() => {
    if (!mapRendered) return;

    setLoadingLayers(getInitialLoadingLayers());

    const routesData = searchedRoutes;
    loadLayer(
      mapLayers, mapRef, mapContext,
      'routeLayer', routesData, routesData, 6, selectedRoute, updateReferenceFeature
    );

    if (localStorage.getItem("pendingFit") === 'true') {
      fitMap(searchedRoutes, mapView, mapRef);
    }

    // Remove all overlays from previously searched routes
    if (!searchedRoutes || !searchedRoutes.length) {
      removeOverlays(mapRef);
    }
  }, [searchedRoutes, mapRendered]);

  // Cameras layer — after first map paint so basemap tiles aren't blocked.
  // Seed and update from filteredCameras only so a poll never paints the full list (DBC22-5976).
  useEffect(() => {
    if (!mapRendered || !filteredCameras) return;

    const clonedFilteredCameras = typeof structuredClone === 'function' ? structuredClone(filteredCameras) : cloneDeep(filteredCameras);
    const groupedFilteredCameras = addCameraGroups(clonedFilteredCameras);

    loadLayer(
      mapLayers, mapRef, mapContext,
      'highwayCams', groupedFilteredCameras, groupedFilteredCameras, 63,
      referenceData, updateReferenceFeature, setLoadingLayers
    );
  }, [filteredCameras, mapRendered]);

  // Events layer
  useEffect(() => {
    if (!mapRendered) return;

    // Add layers if not loaded
    if (events && mapLayers.current && !mapLayers.current['majorEvents']) {
      const eventFound = loadEventsLayers(events, mapContext, mapLayers, mapRef, referenceData, updateReferenceFeature, setLoadingLayers);
      if (referenceData?.type === 'event' && !eventFound) {
        setStaleLinkMessage(true);
        searchParams.delete('type');
        searchParams.delete('display_category');
        searchParams.delete('id');
        searchParams.delete('camIndex');
        setSearchParams(searchParams, { replace: true });
      }
    }

    // Count filtered events to store in routeDetails
    if (filteredEvents) {
      // Toggle features visibility
      updateEventsLayers(
        mapContext, filteredEvents, mapLayers, setLoadingLayers, clickedFeature, mapView,
        featureContext, setFeatureContext // DBC22-5106
      );

      const eventCounts = {
        closures: 0,
        majorEvents: 0,
        minorEvents: 0,
        roadConditions: 0,
        chainUps: 0,
      }
      filteredEvents.forEach(event => {
        const eventType = event.display_category;
        if (eventType) {
          const hasOwn = Object.hasOwn ?
            Object.hasOwn(routeDetails, eventType) :
            routeDetails.hasOwnProperty(eventType);

          if (hasOwn) {
            eventCounts[eventType] += 1;
            setRouteDetails({ ...routeDetails, ...eventCounts});
          }
        }
      });
    }

  }, [filteredEvents, mapRendered]);

  // Ferries layer
  useEffect(() => {
    if (!mapRendered) return;

    if (ferries && filteredFerries) {
      const featuresDict = loadLayer(
        mapLayers, mapRef, mapContext,
        'inlandFerries', ferries, filteredFerries, 66,
        referenceData, updateReferenceFeature, setLoadingLayers
      );
      setFeatureContext({...featureContext, ferries: featuresDict});
    }

    // Add ferry count to routeDetails
    if (Array.isArray(filteredFerries)) {
      setSelectedFerries(filteredFerries.length);
    }
  }, [filteredFerries, mapRendered]);

  // Current weathers layer
  useEffect(() => {
    if (!mapRendered) return;

    loadLayer(
      mapLayers, mapRef, mapContext,
      'weather', currentWeather, filteredCurrentWeathers, 68,
      referenceData, updateReferenceFeature, setLoadingLayers
    );
  }, [filteredCurrentWeathers, mapRendered]);

  // Regional weathers layer
  useEffect(() => {
    if (!mapRendered) return;

    loadLayer(
      mapLayers, mapRef, mapContext,
      'regional', regionalWeather, filteredRegionalWeathers, 69,
      referenceData, updateReferenceFeature, setLoadingLayers
    );
  }, [filteredRegionalWeathers, mapRendered]);

  // High elevation forecasts layer
  useEffect(() => {
    if (!mapRendered) return;

    loadLayer(
      mapLayers, mapRef, mapContext,
      'hef', hef, filteredHef, 70,
      referenceData, updateReferenceFeature, setLoadingLayers
    );
  }, [filteredHef, mapRendered]);

  // Rest stops layer
  useEffect(() => {
    if (!mapRendered) return;

    loadLayer(
      mapLayers, mapRef, mapContext,
      'restStops', restStops, filteredRestStops, 60,
      referenceData, updateReferenceFeature, setLoadingLayers
    );

    loadLayer(
      mapLayers, mapRef, mapContext,
      'largeRestStops', restStops, filteredRestStops, 60,
      referenceData, updateReferenceFeature, setLoadingLayers
    );
  }, [filteredRestStops, mapRendered]);

  // Border crossings layer
  useEffect(() => {
    if (!mapRendered) return;

    loadLayer(
      mapLayers, mapRef, mapContext,
      'borderCrossings', borderCrossings, filteredBorderCrossings, 71,
      referenceData, updateReferenceFeature, setLoadingLayers
    );
  }, [borderCrossings, mapRendered]);

  // Wildfires layer
  useEffect(() => {
    if (!mapRendered) return;

    const featuresDict = loadLayer(
      mapLayers, mapRef, mapContext,
      'wildfires', wildfires, filteredWildfires, 72,
      referenceData, updateReferenceFeature, setLoadingLayers
    );

    setFeatureContext({...featureContext, wildfires: featuresDict});
  }, [wildfires, mapRendered]);

  // Advisories layer
  useEffect(() => {
    if (!mapRendered) return;

    const featuresDict = loadLayer(
      mapLayers, mapRef, mapContext,
      'advisoriesLayer', advisories, filteredAdvisories, 5,
      referenceData, updateReferenceFeature, setLoadingLayers
    );

    setFeatureContext({...featureContext, advisories: featuresDict});
  }, [advisories, mapRendered]);

  useEffect(() => {
    const advisoriesData = (filteredAdvisories && filteredAdvisories.length) ? filteredAdvisories : [];

    if (mapRef.current) {
      // First filter
      onMoveEnd(mapRef.current, advisoriesData, setAdvisoriesInView);
      // Create a named function to use for both adding and removing
      const handleMoveEnd = (e) => onMoveEnd(e.map, advisoriesData, setAdvisoriesInView);

      // Set handler for filtering on map move
      mapRef.current.on('moveend', handleMoveEnd);

      // Return cleanup function that removes the listener
      return () => {
        if (mapRef.current) {
          mapRef.current.un('moveend', handleMoveEnd);
        }
      };
    }
  }, [filteredAdvisories]);

  // DBC22-5326: clear selected feature when route swap clears reference params
  useEffect(() => {
    if (!referenceData?.type && clickedFeatureRef.current) {
      resetClickedStates(null, clickedFeatureRef, updateClickedFeature);
      dispatch(updateShowRouteObjs(false));
    }
  }, [referenceData?.type]);

  const clearClosedPanelUrl = () => {
    const params = new URLSearchParams(searchParams);
    if (params.get('type') !== 'advisory') {
      params.delete('type');
      params.delete('id');
    }
    params.delete('display_category');
    params.delete('camIndex');

    // Camera details URLs should return to the root map when the panel closes
    if (/^\/cameras\/[^/]+/.test(location.pathname)) {
      const search = params.toString();
      navigate(
        { pathname: '/', search: search ? `?${search}` : '' },
        { replace: true },
      );
    } else {
      setSearchParams(params, { replace: true });
    }

    setOpenedFromCameraList(false);
  };

  // Reset search params when panel is closed
  useEffect(() => {
    if (!searchParamInitialized.current) {
      searchParamInitialized.current = true;
      return;
    }

    if (!clickedFeature) {
      clearClosedPanelUrl();
    }

    if (selectedRoute && clickedFeature && clickedFeature.get('type') !== 'route') {
      dispatch(updateShowRouteObjs(true));
    }
  }, [clickedFeature]);

  useEffect(() => {
    if (panel.current) {
      void panel.current.offsetHeight; // force reflow to update panel height
    }
  }, [selectedRoute]);

  // Dms layer
  useEffect(() => {
    if (!mapRendered) return;

    loadLayer(
      mapLayers, mapRef, mapContext,
      'dms', dms, filteredDms, 60,
      referenceData, updateReferenceFeature, setLoadingLayers
    );
  }, [filteredDms, mapRendered]);

  // Group and offset overlapping point features after all layers finish loading
  useEffect(() => {
    if (!mapRendered) return;

    const allLoaded = Object.values(loadingLayers).every(v => v === false);
    if (allLoaded && mapView.current) {
      overlapGroups.current = groupNearbyFeatures(mapLayers);
      applyOverlapOffsets(overlapGroups.current, mapView);
    }
  }, [loadingLayers, mapRendered]);


  /* Rendering */
  return (
    <div
      className="map-container"
      ref={mapContainerRef}
      data-vladyoslav-drawer-wrapper="">
      {smallScreen && openTabs &&
        <div className='mobile-mask'></div>
      }

      {searchedRoutes && searchedRoutes.length > 0 &&
        <DistanceLabels updateRouteDisplay={updateRouteDisplay} mapRef={mapRef} mapRendered={mapRendered} />
      }

      {!!openPanel && largeScreen &&
        <div
          ref={panel}
          className={`side-panel ${openPanel ? 'open' : ''} ${selectedRoute ? 'has-route' : ''}`}>

          {clickedFeature && smallScreen &&
            <button
              className={`resize-panel + ${selectedRoute ? '' : ' no-route'}`}
              aria-label={`${(maximizedPanel ? 'minimize' : 'maximize') + ' side panel'}`}
              tabIndex={0}
              onClick={() => resizePanel(panel, clickedFeature, setMaximizedPanel)}
              onTouchMove={() => resizePanel(panel, clickedFeature, setMaximizedPanel)}
              onKeyDown={keyEvent => {
                if (['Enter', 'NumpadEnter'].includes(keyEvent.key)) {
                  resizePanel(panel, clickedFeature);
                }
              }}>
              <FontAwesomeIcon icon={maximizedPanel ? faChevronDown : faChevronUp} />
            </button>
          }

          {clickedFeature && !selectedRoute &&
            <button
              className="close-panel"
              aria-label={`${openPanel ? 'close side panel' : ''}`}
              aria-hidden={`${openPanel ? false : true}`}
              tabIndex={`${openPanel ? 0 : -1}`}
              onClick={(e) => {
                e.stopPropagation();
                togglePanel(panel, resetClickedStates, clickedFeatureRef, updateClickedFeature, [
                  myLocationRef, routingContainerRef
                ], searchedRoutes);
                setMaximizedPanel(false);
              }}>
              <FontAwesomeIcon icon={faXmark} />
            </button>
          }

          <div className="panel-content">
            {renderPanel(
              clickedFeature && !clickedFeature.get ? advisoriesInView : clickedFeature,
              smallScreen,
              mapView,
              clickedFeatureRef,
              updateClickedFeature,
              showRouteObjs,
              handleSetShowRouteObjs,
              openedFromCameraList
            )}
          </div>
        </div>
      }

      <div ref={mapElement} className="map">
        {(selectedRoute && showRouteObjs && !clickedFeature) && (
          <Button
            variant="primary-outline"
            className="btn-outline-primary back-to-routes"
            onClick={() => dispatch(updateShowRouteObjs(false))}>
            <FontAwesomeIcon icon={faArrowLeft}/>
            Routes
          </Button>
        )}

        {(selectedRoute && showRouteObjs && clickedFeature) && (
          <Button
            variant="primary-outline"
            className="btn-outline-primary back-to-details"
            aria-label={`back to route details`}
            tabIndex={`${openPanel ? 0 : -1}`}
            onKeyDown={(e) => {
              e.stopPropagation();
              if (smallScreen) {
                resetClickedStates(null, clickedFeatureRef, updateClickedFeature);
              } else {
                togglePanel(panel, resetClickedStates, clickedFeatureRef, updateClickedFeature, [
                  myLocationRef, routingContainerRef
                ], searchedRoutes);
                setMaximizedPanel(false);
              }
            }}
            onClick={(e) => {
              e.stopPropagation();
              if (smallScreen) {
                resetClickedStates(null, clickedFeatureRef, updateClickedFeature);
              } else {
                togglePanel(panel, resetClickedStates, clickedFeatureRef, updateClickedFeature, [
                  myLocationRef, routingContainerRef
                ], searchedRoutes);
                setMaximizedPanel(false);
              }
            }}>

            <FontAwesomeIcon icon={faArrowLeft}/>
            {selectedRoute?.label || `Route ${(selectedRoute?.criteria === 'fastest') ? 'A' : 'B'}`}
          </Button>
        )}

        {!largeScreen && openPanel && mapRendered && (
          <Drawer.Root
            key={pendingAction && pendingAction.action === 'showSavePopup' ? pendingAction.action : 'defaultKey'}
            open={true}
            onOpenChange={(open) => {
              if (!open) {
                resetClickedStates(null, clickedFeatureRef, updateClickedFeature);
              }
            }}
            snapPoints={snapPoints}
            snap={snap}
            setSnap={handleSnapChange}
            modal={false}
            dismissible={!drawerShowsRouteDetailsPanel}
            shouldScaleBackground={false}
            scaleFrom={'50%'}>

            <Drawer.Portal container={mapContainerRef.current}>
              <Drawer.Overlay className="drawer-overlay" radixPrimitive={false} />
              <Drawer.Content
                className="drawer-content"
                ref={drawerRef}
                style={{ '--drawer-snap-point': snap }}
              >
                {clickedFeature &&
                  <button
                    className="close-panel"
                    aria-label={`${openPanel ? 'close side panel' : ''}`}
                    aria-hidden={`${openPanel ? false : true}`}
                    tabIndex={`${openPanel ? 0 : -1}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      resetClickedStates(null, clickedFeatureRef, updateClickedFeature);
                    }}>
                    <FontAwesomeIcon icon={faXmark} />
                  </button>
                }

                <div
                  className="panel-content"
                  onPointerDown={(e) => {
                    if (e.target.closest('.popup__content')) {
                      e.stopPropagation();
                    }
                  }}>
                  <div className="drawer-drag-handle"></div>

                  {renderPanel(
                    clickedFeature && !clickedFeature.get ? advisoriesInView : clickedFeature,
                    smallScreen,
                    mapView,
                    clickedFeatureRef,
                    updateClickedFeature,
                    showRouteObjs,
                    handleSetShowRouteObjs,
                    openedFromCameraList
                  )}
                </div>
              </Drawer.Content>
            </Drawer.Portal>
          </Drawer.Root>
        )}

        {!smallScreen && (

          <div className={`map-left-container ${(showServerError || showNetworkError) ? 'error-showing' : ''} ${openPanel && 'margin-pushed'}`}>
            <RouteSearch
              showFilterText={true}
              ref={routingContainerRef}
              routeEdit={true}
              showSpinner={showSpinner}
              onShowSpinnerChange={setShowSpinner}
              myLocation={myLocation}
              mapRef={mapRef}
              mapView={mapView}
              resetClickedStates={() => resetClickedStates(null, clickedFeatureRef, updateClickedFeature)} />
          </div>
        )}

        {(smallScreen && mapRef.current) && (
          <FilterTabs
          mapLayers={mapLayers}
          enableRoadConditions={true}
          enableChainUps={true}
          referenceData={referenceData}
          loadingLayers={loadingLayers}
          open={openTabs}
          setOpen={setOpenTabs} />
          )}

        {(smallScreen && mapRef.current) && (
          <div className="fixed-to-mobile-group"
            style={{
              transform: `translateY(${drawerY}px)`,
              display: (showRouteObjs && selectedRoute && snapPoints.length > 0 && snap === snapPoints[snapPoints.length - 1])
                ? 'none' : 'block'
            }}
          >
            <Button
              ref={myLocationRef}
              className="map-btn my-location fixed-to-mobile"
              variant="primary"
              onClick={loadMyLocation}
              aria-label="my location">
              { myLocationLoading
                ? <Spinner animation="border" role="status" />
                : <FontAwesomeIcon icon={faLocationCrosshairs} />
              }
              My location
            </Button>
          </div>
        )}

        {(!smallScreen && mapRef.current) && (
          <React.Fragment>
            <FilterTabs
              mapLayers={mapLayers}
              enableRoadConditions={true}
              enableChainUps={true}
              referenceData={referenceData}
              loadingLayers={loadingLayers}
              open={openTabs}
              setOpen={setOpenTabs} />

            <Button
              ref={myLocationRef}
              className={`map-btn my-location ${openPanel && 'margin-pushed'}`}
              variant="primary"
              onClick={loadMyLocation}
              aria-label="my location">
                {(myLocationLoading) ? <Spinner animation="border" role="status" /> : <FontAwesomeIcon icon={faLocationCrosshairs} />}
              My location
            </Button>
          </React.Fragment>
        )}

        <div className={"map-btn zoom-btn" + (openTabs ? ' tabs-pushed' : '')}>
          <Button
            className="zoom-in"
            variant="primary"
            aria-label="zoom in"
            onClick={() => zoomIn(mapView)}>
            <FontAwesomeIcon icon={faPlus} />
          </Button>

          <Button
            className="zoom-out"
            variant="primary"
            onClick={() => zoomOut(mapView)}
            aria-label="zoom out">
            <FontAwesomeIcon icon={faMinus} />
          </Button>

          <div className="zoom-divider" />
        </div>
      </div>

      {showLocationAccessError &&
        <LocationAccessPopup marginPushed={!!openPanel} setShowLocationAccessError={setShowLocationAccessError} />
      }

      {showNetworkError &&
        <NetworkErrorPopup marginPushed={!!openPanel} />
      }

      {!showNetworkError && showServerError &&
        <ServerErrorPopup marginPushed={!!openPanel} />
      }

      {staleLinkMessage &&
        <StaleLinkErrorPopup marginPushed={!!openPanel} message={staleLinkMessage}/>
      }
    </div>
  );
}
